import { useDailyError, useDailyEvent, useLocalSessionId, useParticipantIds, useDaily, useTranscription } from "@daily-co/daily-react";
import cn from "../../../../../../../../core/utils/cn_utils";
import { useEffect, useState, useCallback } from "react";
import { DailyAudio } from "@daily-co/daily-react";
import Video from "./Video";
import { Button } from "./ui/Button";
import { Minimize, Maximize, Phone, Mic, MicOff, Video as VideoIcon, VideoOff, Settings, User, Loader2, AlertCircle } from 'lucide-react';
import Modal from "./ui/Modal";
import CameraSettings from "./CameraSettings";
import { useMediaPermissions } from "../../../../hooks/useMediaPermissions";

export default function Call({ handleEnd, avatarName = 'Eve' }) {
    const daily = useDaily();
    const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
    const localSessionId = useLocalSessionId();
    const [mode, setMode] = useState('full');
    const { permissionState, error: permissionError, checkPermissions } = useMediaPermissions();
    const [error, setError] = useState('');
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoOff, setIsVideoOff] = useState(false);
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [orientation, setOrientation] = useState('portrait');
    const [participantStates, setParticipantStates] = useState({});
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isCallEnding, setIsCallEnding] = useState(false);

    // Ensure remoteParticipantIds is always an array
    const participantIdsArray = Array.isArray(remoteParticipantIds)
        ? remoteParticipantIds
        : (remoteParticipantIds ? [remoteParticipantIds] : []);

    const onTranscriptionMessage = (...args) => {
        console.log('asdasdasd', args)
    }
    const { isTranscribing, startTranscription, stopTranscription } = useTranscription({ onTranscriptionMessage });

    // Check permissions and open settings if needed
    useEffect(() => {
        const init = async () => {
            await checkPermissions();

            // Only show settings modal if permissions are denied, not just when they're not granted yet
            if (permissionState.camera === 'denied' || permissionState.microphone === 'denied') {
                setIsSettingsOpen(true);
            }
        };

        init();
    }, []);

    // Handle permission errors
    useEffect(() => {
        if (permissionError) {
            setError(permissionError.message);
            // Wait for 3 seconds to show the error message, then end the call
            const timer = setTimeout(() => {
                handleEnd();
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [permissionError]);

    // Handle orientation change
    useEffect(() => {
        const handleOrientationChange = () => {
            setOrientation(window.innerWidth > window.innerHeight ? 'landscape' : 'portrait');
        };

        // Initial check
        handleOrientationChange();

        // Add event listener
        window.addEventListener('resize', handleOrientationChange);
        window.addEventListener('orientationchange', handleOrientationChange);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleOrientationChange);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    // Handle participant states
    useEffect(() => {
        if (!daily || !localSessionId) return;

        const updateParticipantStates = () => {
            const participants = daily.participants();
            const newStates = {};

            // Set local participant states
            newStates[localSessionId] = {
                video: isVideoOff,
                audio: !isMuted
            };

            // Set remote participant states
            participantIdsArray.forEach(id => {
                if (participants[id]) {
                    newStates[id] = {
                        video: participants[id].video === false,
                        audio: participants[id].audio !== false
                    };
                }
            });
            setParticipantStates(newStates);
        };

        // Initial state update
        updateParticipantStates();

        // Listen for state changes
        const handleParticipantUpdated = (event) => {
            updateParticipantStates();
        };

        // Handle audio device changes
        const handleDeviceChange = () => {
            updateParticipantStates();
        };

        daily.on('participant-updated', handleParticipantUpdated);
        daily.on('track-started', handleDeviceChange);
        daily.on('track-stopped', handleDeviceChange);

        return () => {
            daily.off('participant-updated', handleParticipantUpdated);
            daily.off('track-started', handleDeviceChange);
            daily.off('track-stopped', handleDeviceChange);
        };
    }, [daily, localSessionId, participantIdsArray, isVideoOff, isMuted]);

    // Handle meeting lifecycle events
    useEffect(() => {
        if (!daily) return;

        const handleMeetingState = (event) => {
            console.log('Meeting state changed:', event?.action);

            // Handle different meeting states
            switch (event?.action) {
                case 'joined-meeting':
                    setError('');
                    break;
                case 'left-meeting':
                    if (!isCallEnding) {
                        handleEnd();
                    }
                    break;
                case 'error':
                    if (event?.error?.type === 'meeting-ended') {
                        handleEnd();
                    }
                    break;
            }
        };

        const handleParticipantLeft = (event) => {
            if (event?.participant?.local) {
                handleEnd();
            }
        };

        const handleNetworkQuality = (event) => {
            if (event?.threshold === 'critical') {
                console.warn('Network quality critical');
            }
        };

        daily.on('meeting-state-updated', handleMeetingState);
        daily.on('participant-left', handleParticipantLeft);
        daily.on('network-quality-changed', handleNetworkQuality);
        daily.on('network-connection', handleNetworkQuality);

        // Cleanup function
        return () => {
            daily.off('meeting-state-updated', handleMeetingState);
            daily.off('participant-left', handleParticipantLeft);
            daily.off('network-quality-changed', handleNetworkQuality);
            daily.off('network-connection', handleNetworkQuality);
        };
    }, [daily, handleEnd, isCallEnding]);

    // Handle errors
    useDailyEvent('error', (error) => {
        console.log('Daily.co error:', error);
        const errorMessage = error?.errorMsg || 'An error occurred';

        // Handle specific error types
        if (error?.type === 'meeting-ended') {
            handleEnd();
        } else if (error?.type === 'network') {
            // Attempt to reconnect on network errors
            if (daily && !isCallEnding) {
                daily.reconnect()
                    .catch(e => {
                        console.error('Failed to reconnect:', e);
                        handleEnd();
                    });
            }
        }

        setError(errorMessage);
    });

    const handleToggleMode = () => {
        setMode(prev => prev === 'full' ? 'minimal' : 'full');
    }

    const toggleMute = useCallback(async () => {
        if (!daily) return;

        try {
            const newMuteState = !isMuted;
            await daily.setLocalAudio(!newMuteState);
            setIsMuted(newMuteState);
            
            // Update participant states to reflect the new mute state
            setParticipantStates(prev => ({
                ...prev,
                [localSessionId]: {
                    ...prev[localSessionId],
                    audio: !newMuteState
                }
            }));
        } catch (error) {
            console.error('Error toggling mute:', error);
            setError('Failed to toggle microphone');
        }
    }, [daily, isMuted, localSessionId]);

    // Update initial mute state when joining
    useEffect(() => {
        if (!daily) return;

        const updateInitialAudioState = async () => {
            try {
                const audioState = await daily.localAudio();
                setIsMuted(!audioState);
                setParticipantStates(prev => ({
                    ...prev,
                    [localSessionId]: {
                        ...prev[localSessionId],
                        audio: audioState
                    }
                }));
            } catch (error) {
                console.error('Error getting initial audio state:', error);
            }
        };

        updateInitialAudioState();
    }, [daily, localSessionId]);

    const toggleVideo = useCallback(async () => {
        if (!daily) return;

        try {
            const newVideoState = !isVideoOff;
            if (!newVideoState) {
                setIsVideoLoading(true);
            }
            await daily.setLocalVideo(!newVideoState);
            setIsVideoOff(newVideoState);

            if (!newVideoState) {
                setTimeout(() => {
                    setIsVideoLoading(false);
                }, 500);
            }
        } catch (error) {
            console.error('Error toggling video:', error);
            setError('Failed to toggle camera');
            setIsVideoLoading(false);
        }
    }, [daily, isVideoOff]);

    const cleanupMediaResources = useCallback(async () => {
        try {
            // Stop all media tracks
            const tracks = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            tracks.getTracks().forEach(track => {
                track.stop();
                track.enabled = false;
            });

            // Clean up all video elements
            document.querySelectorAll('video').forEach(video => {
                if (video.srcObject) {
                    video.srcObject.getTracks().forEach(track => {
                        track.stop();
                        track.enabled = false;
                    });
                    video.srcObject = null;
                }
            });

            // Clean up all audio elements
            document.querySelectorAll('audio').forEach(audio => {
                if (audio.srcObject) {
                    audio.srcObject.getTracks().forEach(track => {
                        track.stop();
                        track.enabled = false;
                    });
                    audio.srcObject = null;
                }
            });

            // Revoke all media permissions
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                const stream = await navigator.mediaDevices.getUserMedia({ video: false, audio: false });
                stream.getTracks().forEach(track => {
                    track.stop();
                    track.enabled = false;
                });
            }
        } catch (error) {
            console.error('Error during media cleanup:', error);
        }
    }, []);

    // Add cleanup on component unmount
    useEffect(() => {
        return () => {
            cleanupMediaResources();
        };
    }, [cleanupMediaResources]);

    const endCall = useCallback(async () => {
        if (!daily || isCallEnding) return;

        try {
            setIsCallEnding(true);
            
            // First, stop all local tracks
            const localTracks = daily.participants()?.local?.tracks || {};
            Object.values(localTracks).forEach(trackInfo => {
                if (trackInfo?.track) {
                    trackInfo.track.stop();
                    trackInfo.track.enabled = false;
                }
            });

            // Disable local audio and video
            await daily.setLocalAudio(false);
            await daily.setLocalVideo(false);

            // Leave the meeting
            await daily.leave();
            
            // Destroy daily instance
            await daily.destroy();

            // Perform final cleanup
            await cleanupMediaResources();

            if (handleEnd) {
                handleEnd();
            }
        } catch (error) {
            console.error('Error ending call:', error);
            if (handleEnd) {
                handleEnd();
            }
        } finally {
            setIsCallEnding(false);
        }
    }, [daily, handleEnd, isCallEnding, cleanupMediaResources]);

    // Handle window beforeunload
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (daily) {
                endCall();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [daily, endCall]);

    // Calculate local video dimensions based on orientation
    const getLocalVideoDimensions = () => {
        if (orientation === 'portrait') {
            return 'w-32 h-48'; // Portrait mode dimensions
        }
        return 'w-48 h-32'; // Landscape mode dimensions
    };

    // Handle window events
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement !== null);
        };

        const handleResize = () => {
            // Prevent call from closing on resize
            if (daily) {
                daily.setLocalVideo(true);
                daily.setLocalAudio(true);
            }
        };

        window.addEventListener('resize', handleResize);
        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [daily]);

    return (
        <div className={cn(
            "relative w-full h-full bg-slate-900 rounded-lg overflow-hidden",
            mode === 'minimal' && "w-64 h-48"
        )}>
            {/* Error message */}
            {error && (
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
                    <div className="flex items-center space-x-2 px-4 py-2 bg-red-500/90 text-white rounded-full backdrop-blur-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-sm font-medium">{error}</span>
                    </div>
                </div>
            )}

            {/* Settings button */}
            <button
                onClick={() => setIsSettingsOpen(true)}
                className="absolute top-4 right-4 z-50 p-2 rounded-full bg-slate-800/70 hover:bg-slate-700/70 transition-colors backdrop-blur-sm"
            >
                <Settings className="w-5 h-5 text-white" />
            </button>

            {/* Main video area */}
            <div className="absolute inset-0 flex items-center justify-center">
                {/* Add DailyAudio for remote participants' audio */}
                <DailyAudio />
                
                {participantIdsArray.length > 0 ? (
                    <div className="relative w-full h-full">
                        {/* Remote participant video */}
                        <div className="absolute inset-0">
                            <Video
                                id={participantIdsArray[0]}
                                classNames="w-full h-full object-cover bg-slate-900"
                                audioState={!participantStates[participantIdsArray[0]]?.audio}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center text-white">
                        <div className="w-24 h-24 rounded-full bg-slate-800/70 flex items-center justify-center mb-4 backdrop-blur-sm">
                            <Phone className="w-12 h-12 text-pink-300" />
                        </div>
                        <p className="text-xl font-medium text-pink-300">Waiting for {avatarName} to join...</p>
                    </div>
                )}

                {/* Self view */}
                {localSessionId && (
                    <div className={cn(
                        "absolute rounded-lg overflow-hidden border-2 border-pink-500/30 shadow-lg backdrop-blur-sm",
                        orientation === 'portrait'
                            ? "bottom-4 right-4 w-32 h-48"
                            : "bottom-4 right-4 w-48 h-32"
                    )}>
                        <Video
                            id={localSessionId}
                            classNames="w-full h-full object-cover"
                            audioState={!isMuted}
                        />
                    </div>
                )}
            </div>

            {/* Floating Control bar */}
            <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2" style={{ zIndex: 51 }}>
                <div className="flex items-center space-x-3 sm:space-x-6 bg-slate-800/70 px-3 sm:px-6 py-2 sm:py-3 rounded-full backdrop-blur-sm">
                    <Button
                        variant="outline"
                        size="icon"
                        className={cn(
                            "rounded-full w-10 h-10 sm:w-12 sm:h-12 transition-all duration-300",
                            isMuted ? "bg-red-600/80 hover:bg-red-700/80" : "bg-slate-700/70 hover:bg-slate-600/70"
                        )}
                        onClick={toggleMute}
                    >
                        {isMuted ? <MicOff className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> : <Mic className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
                    </Button>

                    <Button
                        variant="outline"
                        size="icon"
                        className={cn(
                            "rounded-full w-10 h-10 sm:w-12 sm:h-12 transition-all duration-300",
                            isVideoOff ? "bg-red-600/80 hover:bg-red-700/80" : "bg-slate-700/70 hover:bg-slate-600/70"
                        )}
                        onClick={toggleVideo}
                    >
                        {isVideoOff ? <VideoOff className="w-5 h-5 sm:w-6 sm:h-6 text-white" /> : <VideoIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />}
                    </Button>

                    <Button
                        variant="outline"
                        size="icon"
                        className="rounded-full w-10 h-10 sm:w-12 sm:h-12 bg-red-600/80 hover:bg-red-700/80 transition-all duration-300"
                        onClick={endCall}
                        disabled={isCallEnding}
                    >
                        <Phone className="w-5 h-5 sm:w-6 sm:h-6 text-white rotate-135" />
                    </Button>
                </div>
            </div>

            {/* Settings Modal */}
            {isSettingsOpen && (
                <Modal
                    isOpen={isSettingsOpen}
                    onClose={() => setIsSettingsOpen(false)}
                    title="Camera Settings"
                    className="z-[60]"
                >
                    <CameraSettings
                        onClose={() => setIsSettingsOpen(false)}
                        actionLabel="Apply"
                        onAction={() => setIsSettingsOpen(false)}
                        cancelLabel="Cancel"
                        onCancel={() => setIsSettingsOpen(false)}
                    />
                </Modal>
            )}
        </div>
    );
}